import React, { useState, useEffect } from 'react';
import LeftDashboard from '../components/LeftDashboard';
import { fetchStats, fetchCompanies } from '../api';

export default function GhostRatePage({ user, isEmbedded, view, setView, setSelectedCompany, refreshSignal, preloadedStats }) {
  const [stats, setStats] = useState(preloadedStats || null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        if (preloadedStats) {
           setStats(preloadedStats);
           const c = await fetchCompanies({ limit: 50 });
           setCompanies(c.companies || []);
        } else {
           const [s, c] = await Promise.all([fetchStats(), fetchCompanies({ limit: 50 })]);
           setStats(s.stats);
           setCompanies(c.companies || []);
        }
      } catch (err) {
        console.error('Failed to load ghost rate data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [refreshSignal, preloadedStats]);

  const worstOffenders = [...companies].sort((a, b) => (b.ghostPercent || 0) - (a.ghostPercent || 0)).slice(0, 5);
  const bestPractices = [...companies].filter(c => c.totalReviews > 2).sort((a, b) => (a.ghostPercent || 0) - (b.ghostPercent || 0)).slice(0, 5);

  const content = (
    <main style={{ minWidth: 0, animation: 'fadeUp 0.4s ease-out' }}>
      <div style={{
        background: 'linear-gradient(135deg, var(--red), #b91c1c)',
        padding: '16px 24px', borderRadius: 12, color: '#fff',
        boxShadow: '0 8px 16px -4px rgba(239,68,68,0.3)',
        marginBottom: 12, position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', right: -10, top: -20, fontSize: 80, opacity: 0.1 }}>👻</div>
        <h1 style={{ fontFamily: 'var(--display)', fontSize: 28, fontWeight: 900, letterSpacing: -0.5, margin: '0 0 2px' }}>
          {loading ? '...' : (stats?.ghostRate || 0)}%
        </h1>
        <h2 style={{ fontSize: 13, fontWeight: 600, opacity: 0.9, margin: 0, fontFamily: 'var(--sans)' }}>Platform-wide Ghost Rate</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div style={{ background: 'var(--surface)', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 12, color: 'var(--text)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Worst Offenders</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {worstOffenders.map(c => (
              <Offender key={c.id || c.name} name={c.name} rate={c.ghostPercent || 0} />
            ))}
            {!loading && worstOffenders.length === 0 && <div style={{ fontSize: 11, color: 'var(--text3)' }}>No data available</div>}
          </div>
        </div>
        <div style={{ background: 'var(--surface)', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 12, color: 'var(--text)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Best Practices</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {bestPractices.map(c => (
              <Offender key={c.id || c.name} name={c.name} rate={c.ghostPercent || 0} color="var(--green)" />
            ))}
            {!loading && bestPractices.length === 0 && <div style={{ fontSize: 11, color: 'var(--text3)' }}>No data available</div>}
          </div>
        </div>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.4, textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
        We define ghosting as a lack of response for over 3 weeks after Round 2+.
        Our platform aims to name and shame bad recruiter behavior to improve standards in India.
      </p>
    </main>
  );

  if (isEmbedded) return content;

  return (
    <div style={{
      maxWidth: 1100, margin: '0 auto', padding: '16px 24px',
      display: 'grid', gridTemplateColumns: '220px 1fr', gap: 28,
      alignItems: 'start'
    }}>
      <LeftDashboard user={user} view={view} setView={setView} setSelectedCompany={setSelectedCompany} />
      {content}
    </div>
  );
}

function Offender({ name, rate, color = 'var(--red)' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontWeight: 600, color: 'var(--text2)', fontSize: 11 }}>{name}</span>
      <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color, fontSize: 11 }}>{rate}%</span>
    </div>
  );
}
